import React, { useState } from 'react';
import { Plus, Globe, AlertCircle } from 'lucide-react';
import { TaskFile } from '../types';

interface AddLinkProps {
  onAddFile: (file: TaskFile) => void;
  className?: string;
}

export function AddLink({ onAddFile, className = '' }: AddLinkProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const detectFileType = (url: string): TaskFile['type'] => {
    if (url.includes('docs.google.com/document')) return 'document';
    if (url.includes('docs.google.com/spreadsheets')) return 'spreadsheet';
    if (url.includes('docs.google.com/presentation')) return 'presentation';
    if (url.includes('drive.google.com/file')) return 'pdf';
    if (url.includes('.pdf')) return 'pdf';
    if (url.includes('.doc') || url.includes('.docx')) return 'document';
    if (url.includes('.xls') || url.includes('.xlsx')) return 'spreadsheet';
    if (url.includes('.ppt') || url.includes('.pptx')) return 'presentation';
    if (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || url.includes('.gif')) return 'image';
    return 'other';
  };

  const validateUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const extractFilenameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      
      // Extract filename from path
      const filename = pathname.split('/').pop();
      if (filename && filename.includes('.')) {
        // Remove query parameters and decode URL
        const cleanFilename = decodeURIComponent(filename.split('?')[0]);
        return cleanFilename;
      }
      
      return '';
    } catch {
      return '';
    }
  };

  const extractDomainName = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname;
      
      // Remove www. prefix if present
      const domain = hostname.replace(/^www\./, '');
      
      // Capitalize first letter and format nicely
      return domain.charAt(0).toUpperCase() + domain.slice(1);
    } catch {
      return '';
    }
  };

  const generateDefaultName = (url: string): string => {
    // First try to extract filename
    const filename = extractFilenameFromUrl(url);
    if (filename) {
      return filename;
    }
    
    // If no filename, use domain name
    const domain = extractDomainName(url);
    if (domain) {
      return domain;
    }
    
    return '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    if (!validateUrl(url)) {
      setError('Please enter a valid URL (must start with http:// or https://)');
      return;
    }

    // Generate name if not provided
    const finalName = name.trim() || generateDefaultName(url) || 'Link';

    const newFile: TaskFile = {
      id: `file-${Date.now()}`,
      name: finalName,
      url: url.trim(),
      type: detectFileType(url),
      icon: 'link',
      addedBy: 'Current User', // This should come from user context
      addedAt: new Date().toISOString(),
    };

    onAddFile(newFile);
    setUrl('');
    setName('');
    setIsOpen(false);
  };

  const handleUrlChange = (newUrl: string) => {
    setUrl(newUrl);
    
    // Auto-populate name if it's empty or if user hasn't manually edited it
    if (!name.trim() || name === generateDefaultName(url)) {
      const defaultName = generateDefaultName(newUrl);
      if (defaultName) {
        setName(defaultName);
      }
    }
  };

  const handleCancel = () => {
    setUrl('');
    setName('');
    setError('');
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors ${className}`}
      >
        <Plus className="w-4 h-4" />
        <span className="font-medium">Add Link</span>
      </button>
    );
  }

  return (
    <div className={`bg-white/40 backdrop-blur-xl rounded-2xl border border-gray-200 p-4 ${className}`}>
      <div className="flex items-center space-x-2 mb-4">
        <Globe className="w-4 h-4 text-primary-600" />
        <h4 className="font-medium text-gray-900">Add Link</h4>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="file-url" className="block text-sm font-medium text-gray-700 mb-1">
            URL
          </label>
          <input
            id="file-url"
            type="url"
            value={url}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="https://example.com/document.pdf or any web link"
            className="w-full p-3 bg-white/60 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-400/50"
          />
          <p className="text-xs text-gray-500 mt-1">
            Any web link: documents, websites, images, etc.
          </p>
        </div>

        <div>
          <label htmlFor="file-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name (Optional)
          </label>
          <input
            id="file-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Will be auto-generated from URL if left empty"
            className="w-full p-3 bg-white/60 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-400/50"
          />
        </div>

        {error && (
          <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-xl">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors font-medium"
          >
            Add Link
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
